import './style.scss'
import { Button, Dropdown, Input, message, Modal } from 'antd'
import { Buffer } from 'buffer'
import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import wavConverter from 'wav-converter'
import * as homeApi from '@/api/home'
import ArrowLeft from '@/assets/arrow-left.png'
import { Ellipsis } from '@/assets/svg'
import { PlayFill } from '@/assets/svg/play-fill'
import { IUserPackage, UserStore } from '@/global-states'
import { eventTracking } from '@/libs/util'
import {
  CopyOutlined,
  DeleteOutlined,
  DeliveredProcedureOutlined,
  EditOutlined,
  LoadingOutlined,
  PauseOutlined,
  PlusCircleFilled,
  UnlockOutlined,
  VideoCameraAddOutlined
} from '@ant-design/icons'
import VoiceGroups from '../VoiceGroups'

interface IProps {
  group: any
  onAdd?: () => void
  onBack: () => void
  upgradePlan?: () => void
}

const VoiceDetail = forwardRef((props: IProps, ref) => {
  const { t: translate } = useTranslation(['voice', 'system'])
  const navigate = useNavigate()
  const { group, onBack, upgradePlan, onAdd } = props
  const { userPackage } = UserStore
  const [voiceList, setVoiceList] = useState<any[]>()
  const [previewId, setPreviewId] = useState()
  const [playingId, setPlayingId] = useState()
  const previewIdRef = useRef<any>()
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [openId, setOpenId] = useState<any>()
  const [editVoice, setEditVoice] = useState<any>()
  const [voiceId, setVoiceId] = useState<any>()
  const audioRef = useRef<any>()
  const timerRef = useRef<any>()

  useEffect(() => {
    if (group?.id) {
      getDetail()
      timerRef.current = setInterval(() => {
        getDetail()
      }, 5000)
    }
    return () => {
      closeAudio()
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [group])

  useEffect(() => {
    previewIdRef.current = previewId
  }, [previewId])

  useImperativeHandle(ref, () => ({
    getDetail
  }))

  const getDetail = async () => {
    let res
    if (group.book) {
      res = await homeApi.getCommunityVoiceList(group.id)
    } else {
      res = await homeApi.getVoicGroupDetail(group.id)
    }

    setVoiceList([...(res?.list || [])])
  }

  const previewTts = async (tts: any, text: string) => {
    if (playingId === tts.id) {
      closeAudio()
      return
    } else if (previewId === tts.id) {
      return
    }

    setPreviewId(tts.id)
    setPlayingId(undefined)

    try {
      let res
      if (group.book) {
        res = await homeApi.previewCommunityVoice(group.id, tts.id, { text, voice_parameters: tts.voice_parameters })
      } else {
        res = await homeApi.previewTts(tts.id, { text, voice_parameters: tts.voice_parameters })
      }
      if (previewIdRef.current !== tts.id) {
        return
      }
      setPlayingId(tts.id)

      eventTracking('PreviewTts', {
        tts_id: tts?.id
      })

      if (audioRef.current) {
        audioRef.current.pause?.()
        audioRef.current.src = ''
      }

      const audio = new Audio()
      audio.src = `data:audio/wav;base64,${wavConverter
        .encodeWav(new Buffer(res.audio_base64, 'base64'), {
          numChannels: 1,
          sampleRate: 16000,
          byteRate: 32_000
        })
        .toString('base64')}`
      audio.play()
      audioRef.current = audio

      audio.addEventListener('ended', function () {
        closeAudio()
      })

      audio.addEventListener('pause', function () {
        closeAudio()
      })
    } catch {
      closeAudio()
    }
  }

  const closeAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause?.()
      audioRef.current.src = ''
    }
    setPlayingId(undefined)
    setPreviewId(undefined)
  }

  const delVoice = async (id: number, e: any) => {
    e.stopPropagation()
    setOpenId(undefined)
    await homeApi.deleteVoice(id)
    message.success(translate('system.delete_success'))
    getDetail()
  }

  const renameVoice = (v: any, e: any) => {
    e.stopPropagation()
    setOpenId(undefined)
    setEditVoice(v)
    setRenameModalOpen(true)
  }

  const sureRenameVoice = async () => {
    if (group.title.includes(editVoice.display_name)) {
      return message.warning(translate('voice.rename_warning'))
    }
    await homeApi.updateVoice(editVoice.id, {
      voice_name: editVoice.display_name
    })
    message.success(translate('system.update_success'))
    setRenameModalOpen(false)
    getDetail()
  }

  const moveGroup = (id: number, e: any) => {
    e.stopPropagation()
    setOpenId(undefined)
    setVoiceId(id)
  }

  const toCreateVideo = (t: any, e: any) => {
    e.stopPropagation()
    setOpenId(undefined)
    if (userPackage && (userPackage.current_membership_level || 0) < 20 && t.level === 20) {
      return vipTip()
    }
    localStorage.setItem('voiceId', t.id)
    if (group?.book) {
      sessionStorage.setItem('bookId', group.id)
    }
    eventTracking('CreatVoiceClick')
    navigate(`/create-video`)
  }

  const vipTip = () => {
    eventTracking('HighvoiceCloneLimit')
    Modal.confirm({
      title: translate('voice.membership_level_insufficient'),
      content: <div>{translate('voice.high_fidelity_exclusive')}</div>,
      okText: translate('system.upgrade_membership'),
      cancelText: translate('system.cancel'),
      onOk: () => {
        upgradePlan?.()
      }
    })
  }

  const cancelCollectVoice = async () => {
    await homeApi.delteBookmarkedCommunityVoice(group.id)
    onBack()
  }

  const unlockVoice = async (t: any) => {
    const res: IUserPackage = await UserStore.updateUserPackage()
    const totalPoint = (res.membership_credits || 0) + (res.permanent_credits || 0) + (res.free_credits || 0)
    if ((res?.current_membership_level || 0) < 20) {
      return Modal.confirm({
        width: 500,
        title: translate('voice.high_fidelity_v2_trial'),
        content: <div>{translate('voice.high_fidelity_v2_trial_desc')}</div>,
        okText: translate('system.upgrade_membership'),
        cancelText: translate('system.cancel'),
        onOk: () => {
          upgradePlan?.()
        }
      })
    }

    const instance = Modal.confirm({
      width: 560,
      title: translate('voice.high_fidelity_v2_unlock'),
      content: (
        <div>
          <div>{translate('voice.high_fidelity_v2_trial_desc')}</div>
          <div style={{ marginTop: 12 }}>{translate('voice.unlock_requires_points')}</div>
          <div style={{ marginTop: 6 }}>
            {translate('voice.your_points')}:{' '}
            <strong>
              {totalPoint} {translate('system.points')}
            </strong>
          </div>
          <div style={{ marginTop: 2 }}>
            {translate('voice.points_consumed')}: <strong className="red">5000 {translate('system.points')}</strong>
          </div>
        </div>
      ),
      okText: translate('voice.confirm_unlock'),
      cancelText: translate('system.cancel'),
      onOk: async () => {
        await unlock()
      }
    })

    const unlock = async () => {
      instance.update({
        okButtonProps: {
          loading: true
        },
        okText: translate('voice.unlocking'),
        cancelButtonProps: {
          disabled: true
        }
      })
      await homeApi.purchaseVoice({
        trial_id: t.id
      })
      message.success(translate('voice.unlock_success'))
      getDetail()
      return
    }
  }

  return (
    <>
      <div className="common-header">
        <img src={ArrowLeft} onClick={onBack} className="arrow-left" />
        <div className="text">{group.title}</div>
      </div>

      <div className="common-wrappeer">
        <div className="group-detail-wrapper">
          <div className="header">
            <div className="header-left">
              <div className="img">
                <img src={group.cover_url} />
              </div>
              <div className="detail">
                <div className="name">{group.title}</div>
                <div className="desc">
                  {voiceList && (
                    <>
                      {voiceList?.length || 0}
                      {translate('voice.style_count')}
                    </>
                  )}
                </div>
              </div>
            </div>
            {group?.book && (
              <div className="header-right">
                <Button onClick={cancelCollectVoice}>{translate('system.collect_cancel')}</Button>
              </div>
            )}
          </div>
          <div className="list">
            <div className="list-main">
              {!group.global && !group.book && (
                <div className="box-blank" onClick={onAdd}>
                  <PlusCircleFilled />
                  <label>{translate('voice.add_more_styles')}</label>
                </div>
              )}

              {voiceList?.map((t) => (
                <div className={`box ${t.status === 1 ? '' : 'status'} ${openId === t.id ? 'hovered' : ''}`} key={t.id}>
                  <div className="box-audio">
                    <div
                      className="left"
                      onClick={() => {
                        if (userPackage && (userPackage.current_membership_level || 0) < 20 && t.level === 20) {
                          return vipTip()
                        }
                        previewTts(t, t.preview_text || translate('system.default_preview_text'))
                      }}
                    >
                      {previewId === t.id ? (
                        playingId === t.id ? (
                          <PauseOutlined />
                        ) : (
                          <LoadingOutlined />
                        )
                      ) : (
                        <PlayFill className="play" />
                      )}
                    </div>
                    <div className="center">
                      <div className="name ellipsis">{t.display_name}</div>
                    </div>

                    <div className="right">
                      {t.is_premium && (
                        <span className="premium">
                          <label className="gradient">{translate('voice.premium')}</label>
                        </span>
                      )}
                      {t.level === 20 && t.model === 101 && (
                        <span className="high">
                          <label className="yellow-gradient">{translate('voice.high_fidelity_tag')}</label>
                        </span>
                      )}

                      {t.level === 10 && t.model === 8 && (
                        <span className="high">
                          <label className="yellow-gradient">{translate('voice.trial_version')}</label>
                        </span>
                      )}

                      {t.level === 20 && t.model === 102 && (
                        <span className="high">
                          <label className="yellow-gradient">{translate('voice.high_fidelity_v2')}</label>
                        </span>
                      )}
                    </div>
                  </div>

                  {(t.status === 1 || t.status === 3) && (
                    <div className="ellip">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: '5',
                              label: (
                                <div className="dropdown-list" onClick={unlockVoice.bind(this, t)}>
                                  <UnlockOutlined />
                                  {translate('voice.unlock_official')}
                                </div>
                              )
                            },
                            {
                              key: '0',
                              label: (
                                <div className="dropdown-list" onClick={toCreateVideo.bind(this, t)}>
                                  <VideoCameraAddOutlined />
                                  {translate('voice.go_create')}
                                </div>
                              )
                            },
                            {
                              key: '1',
                              label: (
                                <div className="dropdown-list" onClick={renameVoice.bind(this, t)}>
                                  <EditOutlined />
                                  {translate('system.rename')}
                                </div>
                              )
                            },
                            {
                              key: '2',
                              label: (
                                <div className="dropdown-list" onClick={moveGroup.bind(this, t.id)}>
                                  <DeliveredProcedureOutlined />
                                  {translate('system.move_to')}
                                </div>
                              )
                            },
                            {
                              key: '3',
                              label: (
                                <div className="dropdown-list" onClick={delVoice.bind(this, t.id)}>
                                  <DeleteOutlined />
                                  {translate('system.delete')}
                                </div>
                              )
                            },
                            {
                              key: '4',
                              label: (
                                <CopyToClipboard
                                  text={t?.name || ''}
                                  onCopy={() => {
                                    eventTracking('CopyVoiceId', {
                                      name: t?.name || ''
                                    })
                                    message.success(translate('voice.copy_voice_id_success'))
                                  }}
                                >
                                  <div className="dropdown-list">
                                    <CopyOutlined />
                                    {translate('voice.copy_voice_id')}
                                  </div>
                                </CopyToClipboard>
                              )
                            }
                          ].filter((l) => {
                            if (l.key === '5') {
                              if (t.model !== 8 || t.level !== 10) {
                                return false
                              }
                            }

                            if (t.status === 3 && l.key === '0') {
                              return false
                            }

                            if (t.level === 20 && l.key === '3') {
                              return false
                            }
                            if ((group.global || group.book) && !['0', '4'].includes(l.key)) {
                              return false
                            }
                            return true
                          })
                        }}
                        placement="bottom"
                        open={openId === t.id}
                        onOpenChange={(open: boolean) => {
                          setOpenId(open ? t.id : undefined)
                        }}
                      >
                        <Ellipsis />
                      </Dropdown>
                    </div>
                  )}

                  {t.status === 2 && (
                    <div className="cloning">
                      <label>
                        {translate('voice.cloning')}
                        <LoadingOutlined />
                      </label>
                    </div>
                  )}

                  {t.status === 3 && (
                    <div className="error">
                      <label>{translate('voice.clone_failed')}</label>
                      {!!t.error_tip && (
                        <label>
                          <i>-</i>
                          {t.error_tip}
                        </label>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={renameModalOpen}
        title={translate('system.rename')}
        okText={translate('system.confirm')}
        cancelText={translate('system.cancel')}
        onCancel={() => setRenameModalOpen(false)}
        onOk={sureRenameVoice}
        okButtonProps={{
          disabled: !editVoice?.display_name?.trim()
        }}
      >
        <div style={{ padding: 20 }}>
          <Input
            style={{ height: 40 }}
            value={editVoice?.display_name}
            maxLength={20}
            onChange={(e) =>
              setEditVoice({
                ...editVoice,
                display_name: e.target.value
              })
            }
          />
        </div>
      </Modal>

      <VoiceGroups voiceId={voiceId} onSuccess={getDetail} onCancel={() => setVoiceId(undefined)} />
    </>
  )
})

VoiceDetail.displayName = 'VoiceDetail'

export default memo(VoiceDetail)
